type RPagination = {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
};

type RMeta = {
    pagination: RPagination;
};

interface RTimeStamps {
    createdAt: string;
    updatedAt: string;
    publishedAt: string | null;
}

interface StrapiDocument {
    id: number;
    documentId: string;
}

type StrapiRelation = { connect?: [string]; disconnect?: [string]; set?: [string] };
type StrapiRelations = { connect?: string[]; disconnect?: string[]; set?: string[] };

// Utility type to pick and transform specific properties into Strapi relations
type PickAsRelation<T, K extends keyof T> = {
    [P in K]: NonNullable<T[P]> extends any[]
        ? StrapiRelations
        : StrapiRelation;
};

type StrapiError<T = unknown> = {
    status: number;
    name: string;
    message: string;
    details?: T;
};

/**
 * Default Strapi response wrapper, that will be returned by each call to the CMS.
 *
 * You can specify the data format that would be returned in a success case with T and also an ErrorDetails
 * type, that could be part of a custom strapi error.
 */
type ApiResponse<T, ErrorDetails = any> =
    // we may have data (but it is ensured, that we do not have an error in this case)
    | {
          data: T | null;
          meta?: RMeta;
          error: undefined;
      }
    // when we have an error, data is always null
    | {
          data: null;
          meta?: undefined;
          error: StrapiError<ErrorDetails>;
      };

/**
 * Method to check if an arbitrary object is of type StrapiApiResponse. If this method returns true, the typescript
 * compiler treats the object as a StrapiApiResponse.
 *
 * @param object The object to check.
 */
const isApiResponse = <T, E>(object: any): object is ApiResponse<T, E> => {
    return 'data' in object;
};

export type {
    RTimeStamps,
    RMeta,
    RPagination,
    StrapiRelations,
    StrapiRelation,
    PickAsRelation,
    ApiResponse,
    StrapiDocument,
    StrapiError
};
export { isApiResponse };
